import { Link, useParams, Navigate } from "react-router-dom";
import { MapPin, CheckCircle2, ArrowUpRight, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import { FadeUp, MaskLine } from "@/components/motion";
import { LOCATIONS, CONTACT } from "@/data/content";

export default function AreaDetail() {
  const { slug } = useParams();
  const area = LOCATIONS.find((l) => l.slug === slug);
  if (!area) return <Navigate to="/areas" replace />;
  const others = LOCATIONS.filter((l) => l.slug !== slug).slice(0, 6);

  return (
    <main className="pt-20" data-testid={`area-page-${area.slug}`}>
      <SEO
        title={`Removalists ${area.city}, Local & Interstate Moves`}
        description={`Professional removalists in ${area.city}, ${area.state}. ${area.intro.slice(0, 120)}… Fixed written quotes, fully insured.`}
        path={`/areas/${area.slug}`}
      />
      <section className="bg-navy grain py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] font-semibold text-amber-500">
            <MapPin className="h-4 w-4" /> {area.region} · {area.state}
          </p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] text-white">
            <MaskLine delay={0.1}>Removalists in</MaskLine>
            <MaskLine delay={0.22}><span className="text-amber-400">{area.city}.</span></MaskLine>
          </h1>
          <FadeUp delay={0.4}>
            <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300">{area.intro}</p>
          </FadeUp>
          <FadeUp delay={0.55} className="mt-8 flex flex-wrap gap-3">
            <Link to="/quote" data-testid="area-quote-btn"
              className="cta-gleam inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold text-navy">
              Get a {area.city} Quote <ArrowUpRight className="h-4 w-4" />
            </Link>
            <a href={`tel:${CONTACT.phonePrimaryRaw}`} data-testid="area-call-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-bold text-white hover:border-amber-400 hover:text-amber-400 transition-colors duration-200">
              <Phone className="h-4 w-4" /> {CONTACT.phonePrimary}
            </a>
          </FadeUp>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 py-16 sm:py-24 grid gap-12 lg:grid-cols-[1.3fr_1fr]">
        <div>
          <FadeUp>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-navy">
              Moving in {area.city}, what we handle
            </h2>
          </FadeUp>
          <ul className="mt-8 space-y-4">
            {area.highlights.map((h, i) => (
              <FadeUp key={h} delay={i * 0.06} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-5">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                <p className="text-sm sm:text-base font-semibold text-navy">{h}</p>
              </FadeUp>
            ))}
          </ul>
        </div>
        <FadeUp delay={0.15} className="space-y-6">
          <div className="rounded-2xl bg-slate-100 p-8">
            <h3 className="font-display text-lg font-bold text-navy">Suburbs we cover</h3>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{area.suburbs}</p>
          </div>
          <div className="rounded-2xl bg-navy grain p-8">
            <p className="text-xs font-mono uppercase tracking-widest text-amber-500">Written quotes, itemised upfront</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Every {area.city} move gets an itemised written quote before we lift a box.
            </p>
            <Link to="/quote" data-testid="area-aside-quote-btn"
              className="cta-gleam mt-6 inline-flex items-center gap-2 rounded-full bg-amber-500 px-6 py-3 text-sm font-bold text-navy">
              Start my quote <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </FadeUp>
      </section>

      <section className="border-t border-slate-200 bg-slate-50 py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <FadeUp>
            <h2 className="font-display text-xl font-bold text-navy">Moving further afield?</h2>
          </FadeUp>
          <div className="mt-6 flex flex-wrap gap-3">
            {others.map((l) => (
              <Link key={l.slug} to={`/areas/${l.slug}`} data-testid={`area-related-${l.slug}`}
                className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-navy hover:border-amber-500 transition-colors duration-150">
                {l.city}, {l.state}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
